import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  Assignment,
  Download,
  Add,
  CheckCircle,
  Warning,
  Error,
  Visibility,
  Delete,
  TableChart,
  PictureAsPdf,
} from "@mui/icons-material";
import { clraReportsAPI } from "../services/api";
import { useSnackbar } from "notistack";
import { downloadExcelFile, downloadPDFFile } from "../utils/fileDownload";
import dayjs from "dayjs";

const Compliance = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedFormType, setSelectedFormType] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const loadForms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clraReportsAPI.getAll({
        page: 1,
        limit: 50,
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      setForms(response.data.data?.reports || []);
    } catch (error) {
      console.error("Error loading compliance forms:", error);
      enqueueSnackbar(
        `Failed to load compliance forms: ${
          error.response?.data?.error || error.message
        }`,
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const handleGenerateForm = async () => {
    if (!selectedFormType) {
      enqueueSnackbar("Please select a form type", { variant: "warning" });
      return;
    }

    try {
      setGenerating(true);
      const payload = { period: selectedPeriod };

      await clraReportsAPI.generate(selectedFormType, payload);

      enqueueSnackbar("Form generated successfully", { variant: "success" });
      setOpenDialog(false);
      setSelectedFormType("");
      loadForms();
    } catch (error) {
      console.error("Error generating form:", error);
      enqueueSnackbar(
        `Failed to generate form: ${
          error.response?.data?.error || error.message
        }`,
        { variant: "error" }
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadForm = async (formId, format = "excel") => {
    try {
      let response;
      if (format === "excel") {
        response = await clraReportsAPI.downloadExcel(formId);
        downloadExcelFile(response.data, `clra-form-${formId}.xlsx`);
      } else {
        response = await clraReportsAPI.downloadPDF(formId);
        downloadPDFFile(response.data, `clra-form-${formId}.pdf`);
      }
      enqueueSnackbar("File downloaded successfully", { variant: "success" });
    } catch (error) {
      console.error("Error downloading form:", error);
      enqueueSnackbar("Failed to download form", { variant: "error" });
    }
  };

  const handleDeleteForm = async (formId) => {
    if (window.confirm("Are you sure you want to delete this form?")) {
      try {
        await clraReportsAPI.delete(formId);
        enqueueSnackbar("Form deleted successfully", { variant: "success" });
        loadForms();
      } catch (error) {
        console.error("Error deleting form:", error);
        enqueueSnackbar("Failed to delete form", { variant: "error" });
      }
    }
  };

  // CLRA Form types for the dropdown
  const clraFormTypes = [
    { value: "FORM_XII", label: "Form XII - Register of Contractors" },
    { value: "FORM_XIII", label: "Form XIII - Register of Workmen" },
    { value: "FORM_XIV", label: "Form XIV - Muster Roll" },
    { value: "FORM_XV", label: "Form XV - Register of Wages" },
    { value: "FORM_XX", label: "Form XX - Annual Return" },
  ];

  const getStatusChip = (status) => {
    const statusConfig = {
      generated: { color: "success", icon: <CheckCircle /> },
      draft: { color: "warning", icon: <Warning /> },
      pending: { color: "warning", icon: <Warning /> },
      submitted: { color: "info", icon: <CheckCircle /> },
      completed: { color: "success", icon: <CheckCircle /> },
      overdue: { color: "error", icon: <Error /> },
    };
    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        icon={config.icon}
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={config.color}
        size="small"
      />
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Compliance Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
        >
          Generate Form
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Assignment
                  sx={{ fontSize: 40, color: "success.main", mr: 2 }}
                />
                <Box>
                  <Typography variant="h4">
                    {
                      forms.filter(
                        (form) =>
                          form.status === "generated" ||
                          form.status === "completed"
                      ).length
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Forms Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Warning sx={{ fontSize: 40, color: "warning.main", mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {
                      forms.filter(
                        (form) =>
                          form.status === "pending" || form.status === "draft"
                      ).length
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Forms
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Error sx={{ fontSize: 40, color: "error.main", mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {forms.filter((form) => form.status === "overdue").length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overdue Forms
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Assignment
                  sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                />
                <Box>
                  <Typography variant="h4">{forms.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Forms
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Form Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Generated By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.map((form) => (
              <TableRow key={form._id}>
                <TableCell>
                  <Typography variant="subtitle2">{form.reportType}</Typography>
                </TableCell>
                <TableCell>{getStatusChip(form.status)}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {form.period?.month && form.period?.year
                      ? `${form.period.month}/${form.period.year}`
                      : "N/A"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {form.generatedBy?.firstName && form.generatedBy?.lastName
                      ? `${form.generatedBy.firstName} ${form.generatedBy.lastName}`
                      : "System"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {dayjs(form.createdAt).format("DD/MM/YYYY HH:mm")}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Download Excel">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadForm(form._id, "excel")}
                    >
                      <TableChart />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download PDF">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadForm(form._id, "pdf")}
                    >
                      <PictureAsPdf />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Form">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteForm(form._id)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {forms.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No compliance forms found. Generate your first form to get
              started.
            </Typography>
          </Box>
        )}
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Compliance Form</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Form Type</InputLabel>
            <Select
              value={selectedFormType}
              onChange={(e) => setSelectedFormType(e.target.value)}
              label="Select Form Type"
            >
              {clraFormTypes.map((form) => (
                <MenuItem key={form.value} value={form.value}>
                  {form.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedPeriod.month}
                  onChange={(e) =>
                    setSelectedPeriod((prev) => ({
                      ...prev,
                      month: e.target.value,
                    }))
                  }
                  label="Month"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={selectedPeriod.year}
                onChange={(e) =>
                  setSelectedPeriod((prev) => ({
                    ...prev,
                    year: parseInt(e.target.value),
                  }))
                }
                inputProps={{ min: 2020, max: 2030 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerateForm}
            disabled={generating || !selectedFormType}
          >
            {generating ? "Generating..." : "Generate Form"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Compliance;
